import React, { useEffect, useState } from 'react';

interface SignalCardProps {
  coinId: string;
  symbol: string;
  iconUrl: string;
  signal: 'buy' | 'sell';
  entryPrice: string;
  takeProfit: string;
  stopLoss: string;
  potentialProfit: string;
}

const SignalCard: React.FC<SignalCardProps> = ({
  coinId,
  symbol,
  iconUrl,
  signal,
  entryPrice,
  takeProfit,
  stopLoss,
  potentialProfit,
}) => {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-lg">
      <div className="flex items-center mb-4">
        <img src={iconUrl} alt={symbol} className="w-8 h-8 mr-3" />
        <span className="text-xl font-semibold text-slate-900 dark:text-slate-50">
          {symbol}/USDT
        </span>
      </div>
      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-slate-700 dark:text-slate-300">Signal</span>
          <span
            className={`font-medium ${signal === 'buy' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
          >
            {signal.toUpperCase()}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-700 dark:text-slate-300">Entry Price</span>
          <span className="font-medium text-slate-900 dark:text-slate-50">${entryPrice}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-700 dark:text-slate-300">Take Profit</span>
          <span className="font-medium text-green-600 dark:text-green-400">${takeProfit}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-700 dark:text-slate-300">Stop Loss</span>
          <span className="font-medium text-red-600 dark:text-red-400">${stopLoss}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-700 dark:text-slate-300">Potential Profit</span>
          <span className="font-medium text-green-600 dark:text-green-400">{potentialProfit}%</span>
        </div>
      </div>
    </div>
  );
};

const TradingSignalsSection: React.FC = () => {
  const [cryptoData, setCryptoData] = useState<{
    [key: string]: { image: string; symbol: string };
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCryptoData = async () => {
      try {
        const response = await fetch(
          'https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=50&page=1&sparkline=false'
        );
        const data = await response.json();

        const formattedData: { [key: string]: { image: string; symbol: string } } = {};
        data.forEach((coin: any) => {
          formattedData[coin.id] = {
            image: coin.image,
            symbol: coin.symbol.toUpperCase(),
          };
        });

        setCryptoData(formattedData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching crypto data:', error);
        setLoading(false);
      }
    };

    fetchCryptoData();
  }, []);

  // Trading signal data
  const signalData = [
    {
      coinId: 'bitcoin',
      symbol: 'BTC',
      signal: 'buy' as const,
      entryPrice: '35,240',
      takeProfit: '37,500',
      stopLoss: '34,100',
      potentialProfit: '6.4',
    },
    {
      coinId: 'ethereum',
      symbol: 'ETH',
      signal: 'sell' as const,
      entryPrice: '1,850',
      takeProfit: '1,720',
      stopLoss: '1,920',
      potentialProfit: '7.2',
    },
    {
      coinId: 'binancecoin',
      symbol: 'BNB',
      signal: 'buy' as const,
      entryPrice: '245',
      takeProfit: '262',
      stopLoss: '238',
      potentialProfit: '6.9',
    },
    {
      coinId: 'dogecoin',
      symbol: 'DOGE',
      signal: 'buy' as const,
      entryPrice: '0.078',
      takeProfit: '0.084',
      stopLoss: '0.075',
      potentialProfit: '7.7',
    },
  ];

  return (
    <section id="signals" className="py-16 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4">
            Latest Trading Signals
          </h2>
          <p className="text-slate-700 dark:text-slate-300 text-lg max-w-2xl mx-auto">
            Get real-time cryptocurrency trading signals with detailed entry and exit points for
            maximum profit potential.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-slate-700 dark:text-slate-300 py-8">
            Loading trading signals...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {signalData.map((signal, index) => {
              const iconUrl =
                cryptoData && cryptoData[signal.coinId]
                  ? cryptoData[signal.coinId].image
                  : `https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons/128/color/${signal.symbol.toLowerCase()}.png`;

              return (
                <SignalCard
                  key={index}
                  coinId={signal.coinId}
                  symbol={signal.symbol}
                  iconUrl={iconUrl}
                  signal={signal.signal}
                  entryPrice={signal.entryPrice}
                  takeProfit={signal.takeProfit}
                  stopLoss={signal.stopLoss}
                  potentialProfit={signal.potentialProfit}
                />
              );
            })}
          </div>
        )}

        {/* <div className="text-center">
          <button className="bg-primary-main hover:bg-primary-dark text-white font-medium py-3 px-6 rounded-lg transition-colors">
            Get Premium Signals
          </button>
        </div> */}
      </div>
    </section>
  );
};

export default TradingSignalsSection;
